import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import Discussion from 'flarum/common/models/Discussion';
import speciesListFilter from '../utils/speciesListFilter';

// app.modal.show()'s own first parameter type (UnsafeModalClass |
// AsyncModalClass) isn't exported from flarum/core - extracted through
// Parameters<> instead of naming it directly, so this stays correct even
// if that internal type is ever renamed.

// determinationKeyFile()/keyMetadata() are added globally to Discussion.prototype
// by champis-keys' own extend.js/addKeyMetadataDetails.tsx (a plain Model
// attribute/Object.defineProperty patch on the one shared Discussion class
// every extension imports) - real at runtime whenever that extension is
// active, but champis-species has no build-time dependency on it (see this
// class's own docblock), so these aren't part of champis-keys' own
// keyTypes.ts augmentation as far as *this* compilation unit is concerned -
// declared again here, same as champisFirstName()/etc. elsewhere in this
// codebase.

// Same URL champis.net/keys itself links "Mycoclé" and "Mycoclé, G.
// Fannechère" to, and the same reasoning for a real external link, not a
// route - see champis-keys' DeterminationKeysPage.js, whose fileNote() this
// mirrors line for line (down to reusing its own locale keys directly, so
// wording only ever needs to change in one place).
const MYCOCLE_URL = 'http://mycolim.free.fr/DOC_SML/mycoCLE/Charg_Mycocle.htm';
/**
 * "Clés de détermination" box on the species page - links to every
 * "Clés de détermination"-tagged discussion relevant to this species (see
 * SpeciesKeysService), rendered exactly like /keys itself
 * (champis-keys' DeterminationKeysPage.js): title, a real download link,
 * and - when champis-keys is active and the file is parseable - a "Voir"
 * button opening its DeterminationKeyViewer modal.
 *
 * champis-species has no build-time dependency on champis-keys (each
 * extension is its own webpack bundle - a plain `import` across extensions
 * doesn't resolve). Two things bridge that gap at runtime instead, both
 * already proven safe since discussion.determinationKeyFile() itself only
 * exists when champis-keys is active in the first place:
 *  - `determinationKeyFile()` is a Discussion *model* attribute champis-keys
 *    registers globally (Extend.Model(Discussion).attribute(...) patches
 *    the one shared Discussion.prototype every extension imports), so it's
 *    just as usable here as champis-users' champisFirstName() already is
 *    elsewhere in this extension.
 *  - DeterminationKeyViewer is looked up via `flarum.reg.get('champis-keys',
 *    'forum/components/DeterminationKeyViewer')` - the same global
 *    registry `extend()`/`override()` use internally for lazy core modals
 *    (see addRegistrationTerms.js in champis-users), just called directly
 *    here since champis-keys doesn't code-split its own components into
 *    separate chunks (confirmed against its compiled forum.js - no
 *    asyncModuleImport() needed, the class is already in memory by the
 *    time a user could click "Voir"). Explicitly reused rather than
 *    duplicated - it's a whole interactive stepper, and re-implementing it
 *    here would mean two copies to keep in sync as it evolves.
 */
export default class SpeciesKeysBox extends Component {
  discussions = null;
  oninit(vnode) {
    super.oninit(vnode);
    this.discussions = null;
    this.load(this.attrs.gbifId, this.attrs.colId);
  }
  onbeforeupdate(vnode) {
    super.onbeforeupdate(vnode);
    if (vnode.attrs.gbifId !== this.attrs.gbifId || vnode.attrs.colId !== this.attrs.colId) {
      this.load(vnode.attrs.gbifId, vnode.attrs.colId);
    }
  }
  load(gbifId, colId) {
    this.discussions = null;
    app.request({
      method: 'GET',
      url: app.forum.attribute('apiUrl') + '/species-keys',
      params: {
        filter: speciesListFilter(gbifId, colId)
      }
    }).then(response => {
      const ids = (response.data || []).map(item => String(item.attributes.discussionId));

      // One GET /discussions/{id} per key, not a filter[id]=1,2,3 batch -
      // the discussions search endpoint has no `id` filter registered
      // (only created/hidden/unread/author), so that shape silently
      // ignores the filter and returns the default unfiltered list
      // instead (confirmed live before settling on this).
      return Promise.all(ids.map(id => app.store.find('discussions', id)));
    }).then(discussions => {
      this.discussions = discussions.filter(d => d.determinationKeyFile()).sort((a, b) => a.title().localeCompare(b.title()));
      m.redraw();
    }).catch(() => {
      this.discussions = [];
      m.redraw();
    });
  }
  view() {
    if (!this.discussions || this.discussions.length === 0) {
      return null;
    }
    return [m("h2", {
      className: "ChampisDiscussionList-header"
    }, m("i", {
      className: "icon fas fa-key",
      "aria-hidden": "true"
    }), " ", app.translator.trans('champis-species.forum.species_page.keys_header')), m("div", {
      className: "ChampisKeysList"
    }, m("ul", {
      className: "ChampisKeysList-items"
    }, this.discussions.map(discussion => this.row(discussion))))];
  }
  row(discussion) {
    const file = discussion.determinationKeyFile();
    return m("li", {
      key: discussion.id()
    }, m("a", {
      href: file.url,
      download: file.fileName,
      className: "ChampisKeysList-item"
    }, m("span", {
      className: "ChampisKeysList-title"
    }, discussion.title()), this.metaLine(discussion.keyMetadata()), m("span", {
      className: "ChampisKeysList-actions"
    }, m("span", {
      className: "Button Button--primary ChampisKeysList-button"
    }, m("i", {
      className: "icon fas fa-download",
      "aria-hidden": "true"
    }), " ", app.translator.trans('champis-keys.forum.keys_page.download_button')), file.viewable && this.viewerClass() && m("span", {
      className: "Button ChampisKeysList-viewButton",
      role: "button",
      tabIndex: 0,
      onclick: e => this.openViewer(e, file),
      onkeydown: e => {
        if (e.key === 'Enter' || e.key === ' ') this.openViewer(e, file);
      }
    }, m("i", {
      className: "icon fas fa-eye",
      "aria-hidden": "true"
    }), " ", app.translator.trans('champis-keys.forum.keys_page.view_button')), file.viewable && this.flatViewerClass() && m("span", {
      className: "Button ChampisKeysList-viewButton",
      role: "button",
      tabIndex: 0,
      onclick: e => this.openFlatViewer(e, file),
      onkeydown: e => {
        if (e.key === 'Enter' || e.key === ' ') this.openFlatViewer(e, file);
      }
    }, m("i", {
      className: "icon fas fa-align-left",
      "aria-hidden": "true"
    }), " ", app.translator.trans('champis-keys.forum.keys_page.view_flat_button'))), this.fileNote(file)));
  }

  // Mirrors DeterminationKeysPage.js's own metaLine() line for line - same
  // reasoning as this file's own row()/fileNote(), see this class' docblock.
  metaLine(metadata) {
    if (!metadata || !metadata.author && !metadata.language) return null;
    return m("span", {
      className: "ChampisKeysList-meta"
    }, metadata.author, metadata.author && metadata.language && ' · ', metadata.language);
  }

  // See this class' own docblock for why this is a runtime registry lookup
  // rather than an import - champis-keys.forum.components.DeterminationKeyViewer.
  viewerClass() {
    return window.flarum.reg.get('champis-keys', 'forum/components/DeterminationKeyViewer');
  }

  // Same reasoning as viewerClass() above, for the flat/anchored viewer.
  flatViewerClass() {
    return window.flarum.reg.get('champis-keys', 'forum/components/DeterminationKeyFlatViewer');
  }
  fileNote(file) {
    const t = function (key, params) {
      if (params === void 0) {
        params = {};
      }
      return app.translator.trans(`champis-keys.forum.keys_page.${key}`, params);
    };
    const mycocleLinkAttrs = () => m("a", {
      href: MYCOCLE_URL,
      target: "_blank",
      rel: "noopener noreferrer",
      onclick: e => e.stopPropagation()
    });
    if (/\.pdf$/i.test(file.fileName)) {
      return m("div", {
        className: "ChampisKeysList-note"
      }, t('pdf_note'));
    }
    if (/\.(cle|zip)$/i.test(file.fileName)) {
      return m("div", {
        className: "ChampisKeysList-note"
      }, m("div", null, t(file.viewable ? 'cle_readable_note' : 'cle_coded_note', {
        mycocle: mycocleLinkAttrs()
      })), m("div", null, t('cle_source', {
        mycoclecredit: mycocleLinkAttrs()
      })));
    }
    return null;
  }
  openViewer(e, file) {
    e.preventDefault();
    e.stopPropagation();
    const Viewer = this.viewerClass();
    if (!Viewer) return;
    app.modal.show(Viewer, {
      uuid: file.uuid,
      fileName: file.fileName
    });
  }
  openFlatViewer(e, file) {
    e.preventDefault();
    e.stopPropagation();
    const Viewer = this.flatViewerClass();
    if (!Viewer) return;
    app.modal.show(Viewer, {
      uuid: file.uuid,
      fileName: file.fileName
    });
  }
}
flarum.reg.add('champis-species', 'forum/components/SpeciesKeysBox', SpeciesKeysBox);